import React, { useState, useEffect, useCallback } from "react";
import { Grid, Modal } from "@mui/material";
import Feed from "../services/Feed";
import ImageModal from "../components/ImageModal";
import { Storage } from "aws-amplify";
import { API } from "aws-amplify";
import { listGalleryItems } from "../graphql/queries";
import useWindowDimensions from "../services/useWindowDimensions";
import SEO from "../components/SEO";

function GalleryPage() {
  const { width } = useWindowDimensions();
  const initialLoadAmount = width < 600 ? 15 : 12;
  const loadStep = width < 600 ? 6 : 12;
  const [gallery, setGallery] = useState([]);
  const [loadedAmount, setLoadedAmount] = useState(initialLoadAmount);

  const getImageUrl = useCallback(async (fileKey) => {
    const signedUrl = await Storage.get(fileKey, {
      level: "public",
    });

    return signedUrl;
  }, []);

  const fetchGalleryItems = useCallback(async () => {
    try {
      const galleryData = await API.graphql({
        query: listGalleryItems,
      });

      const itemsArray = (galleryData.data.listGalleryItems.items || []).filter(Boolean);
      itemsArray.sort((a, b) => (a.imageKey > b.imageKey ? -1 : 1));

      for (let i = 0; i < itemsArray.length; i++) {
        const fileKey = itemsArray[i].imageKey;
        const signedUrl = await getImageUrl(fileKey);
        itemsArray[i].imageKey = signedUrl;
      }

      setGallery(itemsArray);
    } catch (error) {
      // A single bad record can null out just that item (GraphQL null propagation on a
      // non-nullable field) while the rest of the list is still valid — use it if present.
      const partialItems = error?.data?.listGalleryItems?.items;
      if (Array.isArray(partialItems)) {
        console.warn("Some gallery items failed to load:", error.errors);
        const itemsArray = partialItems.filter(Boolean);
        itemsArray.sort((a, b) => (a.imageKey > b.imageKey ? -1 : 1));

        for (let i = 0; i < itemsArray.length; i++) {
          const fileKey = itemsArray[i].imageKey;
          const signedUrl = await getImageUrl(fileKey);
          itemsArray[i].imageKey = signedUrl;
        }

        setGallery(itemsArray);
        return;
      }

      console.error("Error fetching gallery items:", error);
    }
  }, [getImageUrl]);

  useEffect(() => {
    fetchGalleryItems();
  }, [fetchGalleryItems]);

  useEffect(() => {
    setLoadedAmount((prevLoadedAmount) =>
      prevLoadedAmount < initialLoadAmount ? initialLoadAmount : prevLoadedAmount,
    );
  }, [initialLoadAmount]);

  useEffect(() => {
    if (gallery.length === 0 || loadedAmount >= gallery.length) {
      return;
    }

    const maybeLoadMore = () => {
      const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
      const viewportBottom = scrollTop + window.innerHeight;
      const contentHeight = document.documentElement.scrollHeight;
      const nearBottom = viewportBottom >= contentHeight - 240;
      const contentTooShort = contentHeight <= window.innerHeight + 120;

      if (nearBottom || contentTooShort) {
        setLoadedAmount((prevLoadedAmount) => Math.min(prevLoadedAmount + loadStep, gallery.length));
      }
    };

    maybeLoadMore();
    window.addEventListener("scroll", maybeLoadMore, { passive: true });
    window.addEventListener("resize", maybeLoadMore);

    return () => {
      window.removeEventListener("scroll", maybeLoadMore);
      window.removeEventListener("resize", maybeLoadMore);
    };
  }, [gallery.length, loadedAmount, loadStep]);

  const [modalContent, setModalContent] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [contentIndex, setContentIndex] = useState();

  const openModal = (e) => {
    const nextIndex = gallery.findIndex((image) => image.id === e.id);
    if (nextIndex < 0) {
      return;
    }

    setContentIndex(nextIndex);
    setModalContent(gallery[nextIndex]);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalContent([]);
    setModalOpen(false);
    setContentIndex();
  };
  const nextImage = () => {
    setModalContent(gallery[contentIndex + 1]);
    setContentIndex(contentIndex + 1);
  };
  const prevImage = () => {
    setModalContent(gallery[contentIndex - 1]);
    setContentIndex(contentIndex - 1);
  };

  return (
    <Grid
      container
      spacing={{ sm: 3, xs: 1 }}
      style={{
        alignItems: "flex-start",
        justifyContent: "flex-start",
        paddingTop: 5,
      }}
    >
      <SEO
        title="Project Gallery"
        description="Browse completed HaasFab projects: powder coating, Cerakote, custom metal signs, CNC plasma cutting, welding, and fabrication work."
        path="/gallery"
      />
      {gallery.map(
        (feed, index) =>
          index < loadedAmount && (
            <Grid item xs={4} key={index}>
              <Feed key={index} feed={feed} openModal={openModal} />
            </Grid>
          ),
      )}
      <Modal open={modalOpen} onClose={closeModal}>
        <ImageModal
          modalContent={modalContent}
          closeModal={closeModal}
          nextImage={nextImage}
          prevImage={prevImage}
          contentIndex={contentIndex}
          maxIndex={gallery.length - 1}
        />
      </Modal>
    </Grid>
  );
}

export default GalleryPage;
